// LITRA clean filenames — targeted fix (before sync S2b).
//
// A self-reinforcing sync→save loop grows chapter filenames by one "NN_" prefix
// per round: sync sets chapter.title = filename (incl. the prefix), and save
// builds filename = `${id}_` + title — so the prefix doubles. Tags also pile up
// because they are appended without dedup. This corrupts the user-visible chapter
// title, grows the filename unbounded, and bloats the tag list on every save.
//
// Fix (pure helpers, applied at the naming/tag helpers so ALL save paths are
// covered — sync, recovery, artifact, ensureManuscriptProject):
//   - stripLeadingNumberPrefix: drop leading "NN_"/"NN-" groups from titles.
//   - dedupeTags: collapse repeated tags, order-preserving.
// With the prefix stripped on BOTH read (title-from-filename) and write
// (filename-from-title), existing polluted files SELF-HEAL on the next save.
//
// Gated by VITE_FF_CLEAN_FILENAMES. Default OFF — bit-identical to today (clean
// rollback, consistency with the rest of the sync track). It is a bug fix, but
// flag-gated so it turns on together with the rest at S6.

/** Pure flag resolution: default OFF, only an explicit "true" enables it. */
export function isCleanFilenamesEnabled(raw: string | undefined): boolean {
  return String(raw ?? "false").toLowerCase() === "true";
}

const flagRaw = (import.meta.env as Record<string, string | undefined>)
  .VITE_FF_CLEAN_FILENAMES;
/** Default OFF — only an explicit "true" enables clean naming + tag dedup. */
export const CLEAN_FILENAMES_ON = isCleanFilenamesEnabled(flagRaw);

/**
 * Strip leading numeric "NN_" / "NN-" prefix group(s) from a name/title.
 * "01_01_01_Skyggernes" → "Skyggernes". Idempotent. A leading number with no
 * separator (e.g. "1984") is preserved — only the chapter-prefix shape is removed.
 */
export function stripLeadingNumberPrefix(value: string): string {
  return String(value ?? "").replace(/^(?:\d+[_-])+/, "");
}

/** Collapse duplicate tags while preserving first-seen order. */
export function dedupeTags(tags: string[]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const tag of tags) {
    if (tag == null) continue;
    if (seen.has(tag)) continue;
    seen.add(tag);
    out.push(tag);
  }
  return out;
}
